import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Container, Typography, Alert, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import { captureSubscription } from "../utils/firebase";

export default function CheckoutSuccessPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const { localizePath } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate(localizePath("/login"), { replace: true });
      return;
    }
  }, [user, authLoading, navigate, localizePath]);

  useEffect(() => {
    if (!user || !sessionId) {
      if (user && !sessionId) {
        setStatus("no_subscription");
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await captureSubscription({ sessionId });
        if (!cancelled) setStatus("success");
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("checkoutSuccess.errorActivate"));
          setStatus("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, sessionId, t]);

  if (authLoading || !user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (status === "no_subscription") {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          {t("checkoutSuccess.noSession")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t("checkoutSuccess.noSessionDesc")}
        </Typography>
        <Button variant="contained" onClick={() => navigate(localizePath("/"))}>
          {t("checkoutSuccess.goToDashboard")}
        </Button>
      </Container>
    );
  }

  if (status === "loading") {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" gap={2}>
        <CircularProgress />
        <Typography>{t("checkoutSuccess.activating")}</Typography>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate(localizePath("/checkout"))}>
          {t("checkoutSuccess.tryAgain")}
        </Button>
        <Button sx={{ ml: 2 }} onClick={() => navigate(localizePath("/"))}>
          {t("checkoutSuccess.goToDashboard")}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
      <Typography variant="h5" gutterBottom>
        {t("checkoutSuccess.successTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("checkoutSuccess.successDesc")}
      </Typography>
      <Button variant="contained" onClick={() => navigate(localizePath("/"))}>
        {t("checkoutSuccess.goToDashboard")}
      </Button>
    </Container>
  );
}
